import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useHistory, Link } from 'react-router-dom';
import { marketplaceService } from '../../_services/marketplace.service';
import SalonImageCarousel from '../common/SalonImageCarousel';
import './CategoryLocationPage.css';

const CATEGORY_META = {
    'hair-salons': { label: 'Hair Salons', schemaType: 'HairSalon', description: 'hair salons' },
    'barbers': { label: 'Barbers', schemaType: 'BarberShop', description: 'barbers' },
    'beauty-salons': { label: 'Beauty Salons', schemaType: 'BeautySalon', description: 'beauty salons' },
    'nail-salons': { label: 'Nail Salons', schemaType: 'NailSalon', description: 'nail salons' },
    'spas': { label: 'Spas & Wellness', schemaType: 'DaySpa', description: 'spas and wellness centres' }
};

const StarIcon = ({ size = 12 }) => (
    <svg viewBox="0 0 24 24" fill="#000" stroke="#000" strokeWidth="1" width={size} height={size}>
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
    </svg>
);

function CategoryLocationPage() {
    const { location } = useParams();
    const history = useHistory();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Extract category from pathname (e.g. "/hair-salons/in/fulham" → "hair-salons")
    const category = window.location.pathname.split('/in/')[0].substring(1);
    const categoryMeta = CATEGORY_META[category];

    useEffect(() => {
        async function fetchData() {
            setLoading(true);
            setError(null);
            try {
                const result = await marketplaceService.getBrowsePage(category, location);
                setData(result);
            } catch (err) {
                setError('Unable to load results. Please try again.');
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, [category, location]);

    const handleSalonClick = useCallback((slug) => {
        history.push(`/book/${slug}`);
    }, [history]);

    if (!categoryMeta) {
        return <div className="clp-container"><p>Category not found.</p></div>;
    }

    const locationLabel = data?.location?.label || deslugify(location);
    const pageTitle = `Best ${categoryMeta.label} in ${locationLabel}`;
    const seoDescription = data
        ? `Find and book the best ${categoryMeta.description} in ${locationLabel}. ${data.total} businesses available. Book online instantly with Beu — no booking fees.`
        : `Find and book the best ${categoryMeta.description} in ${locationLabel}. Book online instantly with Beu.`;
    const canonicalUrl = `https://app.beuapp.com/${category}/in/${location}`;

    // JSON-LD ItemList
    const jsonLd = data ? {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        'name': pageTitle,
        'description': seoDescription,
        'url': canonicalUrl,
        'numberOfItems': data.total,
        'itemListElement': data.salons.slice(0, 20).map((salon, i) => ({
            '@type': 'ListItem',
            'position': i + 1,
            'item': {
                '@type': categoryMeta.schemaType || 'HealthAndBeautyBusiness',
                'name': salon.salonName,
                'url': `https://app.beuapp.com/book/${salon.slug}`,
                ...(salon.formattedAddress && {
                    'address': {
                        '@type': 'PostalAddress',
                        'addressLocality': salon.neighborhood || salon.administrativeAreaLevel2,
                        'addressRegion': salon.administrativeAreaLevel1,
                        'postalCode': salon.postalCode
                    }
                }),
                ...(salon.loc && salon.loc.length === 2 && {
                    'geo': {
                        '@type': 'GeoCoordinates',
                        'longitude': salon.loc[0],
                        'latitude': salon.loc[1]
                    }
                }),
                ...(salon.googleRating && {
                    'aggregateRating': {
                        '@type': 'AggregateRating',
                        'ratingValue': salon.googleRating,
                        'reviewCount': salon.googleRatingsTotal || 1
                    }
                }),
                ...(salon.mainImage && { 'image': salon.mainImage })
            }
        }))
    } : null;

    return (
        <div className="clp-container">
            <Helmet>
                <title>{pageTitle} - Book Online | Beu</title>
                <meta name="description" content={seoDescription} />
                <meta property="og:title" content={`${pageTitle} | Beu`} />
                <meta property="og:description" content={seoDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:type" content="website" />
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:title" content={`${pageTitle} | Beu`} />
                <meta name="twitter:description" content={seoDescription} />
                <link rel="canonical" href={canonicalUrl} />
                {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
            </Helmet>

            {/* Breadcrumb */}
            <nav className="clp-breadcrumb" aria-label="Breadcrumb">
                <Link to="/">Home</Link>
                <span className="clp-breadcrumb-sep">/</span>
                <span>{categoryMeta.label}</span>
                <span className="clp-breadcrumb-sep">/</span>
                <span>{locationLabel}</span>
            </nav>

            <h1 className="clp-title">{pageTitle}</h1>

            {loading && (
                <div className="clp-loading">
                    <div className="clp-skeleton-grid">
                        {[1, 2, 3, 4, 5, 6].map(i => (
                            <div key={i} className="clp-skeleton-card">
                                <div className="clp-skeleton-image" />
                                <div className="clp-skeleton-text" />
                                <div className="clp-skeleton-text short" />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {error && <p className="clp-error">{error}</p>}

            {!loading && data && (
                <>
                    <p className="clp-subtitle">
                        {data.total} {categoryMeta.description} in {locationLabel}
                    </p>

                    {data.salons.length > 0 ? (
                        <div className="clp-salon-grid">
                            {data.salons.map((salon) => (
                                <div
                                    key={salon._id}
                                    className="clp-salon-card"
                                    onClick={() => handleSalonClick(salon.slug)}
                                >
                                    <div className="clp-salon-image">
                                        <SalonImageCarousel
                                            mainImage={salon.mainImage}
                                            galleryImages={salon.heroImages}
                                            altText={salon.salonName}
                                            businessType={salon.businessType}
                                            onClick={() => handleSalonClick(salon.slug)}
                                            overlay={
                                                salon.googleRating > 0 && (
                                                    <div className="clp-salon-rating">
                                                        <StarIcon size={10} />
                                                        <span>{salon.googleRating?.toFixed(1)}</span>
                                                        {salon.googleRatingsTotal > 0 && (
                                                            <span>({salon.googleRatingsTotal})</span>
                                                        )}
                                                    </div>
                                                )
                                            }
                                        />
                                    </div>
                                    <div className="clp-salon-info">
                                        <h3 className="clp-salon-name">{salon.salonName}</h3>
                                        <p className="clp-salon-address">
                                            {salon.neighborhood || salon.administrativeAreaLevel2}
                                            {salon.postalCode && ` · ${salon.postalCode}`}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="clp-no-results">
                            No {categoryMeta.description} found in {locationLabel} yet.
                        </p>
                    )}

                    {/* Internal linking: Nearby locations */}
                    {data.nearbyLocations && data.nearbyLocations.length > 0 && (
                        <section className="clp-links-section">
                            <h2>More {categoryMeta.label} nearby</h2>
                            <div className="clp-links-grid">
                                {data.nearbyLocations.map((loc) => (
                                    <Link
                                        key={loc.slug}
                                        to={`/${category}/in/${loc.slug}`}
                                        className="clp-link-chip"
                                    >
                                        {categoryMeta.label} in {loc.label}
                                    </Link>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Internal linking: Other categories in this location */}
                    {data.otherCategories && data.otherCategories.length > 0 && (
                        <section className="clp-links-section">
                            <h2>More in {locationLabel}</h2>
                            <div className="clp-links-grid">
                                {data.otherCategories.map((cat) => (
                                    <Link
                                        key={cat.slug}
                                        to={`/${cat.slug}/in/${location}`}
                                        className="clp-link-chip"
                                    >
                                        {cat.label} in {locationLabel}
                                    </Link>
                                ))}
                            </div>
                        </section>
                    )}
                </>
            )}
        </div>
    );
}

function deslugify(slug) {
    if (!slug) return '';
    return slug.split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

export default CategoryLocationPage;
